import { useCallback } from 'react'
import { Box, Stack } from '@mui/material'

import { BodyText } from '@/components/atoms/BodyText'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { type YakurekiDetail } from '@/shared/types/yakurekiCore'
import {
  type SoapContentItem,
  type SoapSectionKeys,
} from '@/shared/types/yakurekiSoap'

import { CopyableHeading } from './CopyableHeading'
import { SectionRenderer } from './SectionRenderer'
import { soapLineDomId, soapLineKey } from './changedSoapLines'
import { SOAP_LINE_BULLET_WIDTH, SOAP_SECTIONS } from './soapSections'
import { buildSoapSectionText } from './soapCopyText'
import { useSoapLineEditing } from './hooks/useSoapLineEditing'
import { useCopyAndComplete, useCopyText } from './hooks/useSoapCopy'

interface Props {
  event: YakurekiDetail
  reloadEvent: () => Promise<void>
  medications: string[]
  // AI で改善して変わった行 (soapLineKey)
  flashLines: Set<string>
  onPlaceholderReplace: (
    placeholder: string,
    replacement: string,
    replaceAll?: boolean
  ) => Promise<boolean>
}

// SOAP 本体 (S/O/A/EP/CP/OP の 6 section + フォローアップ計画) の描画ユニット.
// 行の採否 (is_selected) / 編集 / 追加と section 単独コピーを担う. 採否は is_selected を唯一の
// 起点にし (訪問報告書と共通)、 編集 hook (useSoapLineEditing) の toggleSelect で永続する.
export const SoapBody = ({
  event,
  reloadEvent,
  medications,
  flashLines,
  onPlaceholderReplace,
}: Props) => {
  const editing = useSoapLineEditing({ event, reloadEvent })
  // 系統1 (copy = 完了): SOAP section は成果物なのでコピーで完了させる.
  const copyAndComplete = useCopyAndComplete(event.id)
  // 系統2 (clipboard のみ): FP は成果物でない (SOAP とは別軸) ため完了させない.
  const copyText = useCopyText()

  // SOAP section 単独コピー (= 見出し S/O/A... をクリックでその section を copy). 採否 (is_selected)
  // を反映. 全文コピーと同じく copy = 完了 (= 系統1).
  const copySection = useCallback(
    (key: SoapSectionKeys) => {
      const items = (event.soaps?.[key] ?? []) as SoapContentItem[]
      void copyAndComplete(buildSoapSectionText(items))
    },
    [event.soaps, copyAndComplete]
  )

  // FP (フォローアップ計画) 単独コピー. SOAP の master copy には含めず (= OP との間の
  // divider で区切る)、 この見出しからのみ copy する. 成果物でないため完了させない (= 系統2).
  const copyFollowup = useCallback(() => {
    const items = event.soaps?.followup_plans ?? []
    void copyText(
      items.map((item) => item.content).join('\n'),
      'フォローアップ計画をコピーしました'
    )
  }, [event.soaps, copyText])

  return (
    <>
      {/* section (S/O/A/EP/CP/OP) 間の縦余白. 正典 §2.2「見出しブロック前後 = 12-16px」の
          上限 (16px = spacing 2) に確定 (実機確認 2026-07-01: 下限 12px は密な印象という
          フィードバックを受け上限へ). 各 section の描画は 訪問報告書 と共通の SectionRenderer.
          採否は is_selected を SectionStrikeAdapter で渡す (toggle は editing.toggleSelect で永続). */}
      <Stack spacing={2}>
        {SOAP_SECTIONS.map(({ key, label }) => {
          const items = (event.soaps?.[key] ?? []) as SoapContentItem[]
          return (
            <SectionRenderer
              key={key}
              sectionKey={key}
              label={label}
              items={items}
              editing={editing}
              strike={{
                // 採否 = is_selected (index で識別)。訪問報告書 (HomeVisitReportPanel) と同形。
                isStruck: (item) => !(item.is_selected ?? true),
                toggleLine: (_item, i) => editing.toggleSelect(key, i),
              }}
              isFlashing={(i) => flashLines.has(soapLineKey(key, i))}
              lineId={(i) => soapLineDomId(soapLineKey(key, i))}
              onCopy={() => copySection(key)}
              copyTitle={`${label} をコピー`}
              medications={medications}
              onPlaceholderReplace={onPlaceholderReplace}
            />
          )
        })}
      </Stack>

      {/* フォローアップ計画 (FP): SOAP 6 section と同じ見出し/字格で表示するが、
          来局を待たない継続フォローのため OP との間に divider を入れて区切る
          (= 上の master copy ボタンの対象は SOAP までであることを示す)。
          空でも他の SOAP section ((なし)) と同様にセクションを残し、「(検知なし)」を出す。 */}
      <Box
        sx={{
          mt: 2,
          pt: 1.5,
          borderTop: `1px solid ${colorTokens.border.default}`,
        }}
      >
        {/* FP はチェックボックスを持たない (モーフなし). ラベルは S/O/A と同じ左レール (行頭)
            に置き、 本文は行あり section と同じ字下げに揃える. */}
        <CopyableHeading
          label="フォローアップ"
          title="フォローアップをコピー"
          onCopy={() => void copyFollowup()}
        />
        {(event.soaps?.followup_plans?.length ?? 0) > 0 ? (
          <Stack
            spacing={0.25}
            sx={{ pl: `${SOAP_LINE_BULLET_WIDTH}px`, mt: 0.25 }}
          >
            {(event.soaps?.followup_plans ?? []).map((item, i) => (
              <BodyText key={i} sx={{ whiteSpace: 'pre-wrap' }}>
                ・{item.content}
              </BodyText>
            ))}
          </Stack>
        ) : (
          <Box sx={{ pl: `${SOAP_LINE_BULLET_WIDTH}px`, mt: 0.25 }}>
            <BodyText sx={{ color: colorTokens.fg.faint }}>(検知なし)</BodyText>
          </Box>
        )}
      </Box>
    </>
  )
}
